import { memo } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronRight } from 'lucide-react';

import Tooltip from '../Tooltip.jsx';

const BADGE_BASE =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold transition-colors motion-reduce:transition-none';

const BADGE_BY_STATE = {
  completed: 'bg-brand-600/15 text-brand-500',
  current: 'bg-brand-600 text-white',
  upcoming: 'bg-surface-muted text-content-muted',
};

const LABEL_BY_STATE = {
  completed: 'text-content-secondary',
  current: 'font-semibold text-content',
  upcoming: 'text-content-muted',
};

const BUTTON_CLASS = [
  'inline-flex cursor-pointer items-center gap-2 rounded-md',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
  'enabled:hover:text-content',
].join(' ');

const STATIC_CLASS = 'inline-flex items-center gap-2';

function joinClasses(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * One step of {@link Stepper}: a number (or a check once completed), its label
 * and, except on the last step, the chevron that leads to the next one. It is a
 * `<button>` only when the step can be opened; otherwise it is plain text, so a
 * blocked step never takes focus.
 *
 * @param {object} props
 * @param {string} props.stepId — Reported to `onSelect` when the step is opened.
 * @param {number} props.index — Zero-based position; the badge shows `index + 1`.
 * @param {'completed'|'current'|'upcoming'} props.state
 * @param {import('react').ReactNode} props.label
 * @param {boolean} props.isLast — Hides the trailing chevron.
 * @param {boolean} props.interactive — Renders a button and reports clicks.
 * @param {boolean} props.blocked — Marks the step `aria-disabled`.
 * @param {string} [props.reason] — Why the step is blocked; read aloud and shown as a tooltip.
 * @param {(stepId: string) => void} [props.onSelect] — Stable callback; the step passes back its own id.
 */
function StepperStep({
  stepId,
  index,
  state,
  label,
  isLast,
  interactive,
  blocked,
  reason,
  onSelect,
}) {
  const badge = (
    <span className={joinClasses(BADGE_BASE, BADGE_BY_STATE[state])}>
      {state === 'completed' ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : index + 1}
    </span>
  );
  const text = (
    <span className={joinClasses('whitespace-nowrap text-sm', LABEL_BY_STATE[state])}>{label}</span>
  );

  const body = interactive ? (
    <button type="button" className={BUTTON_CLASS} onClick={() => onSelect?.(stepId)}>
      {badge}
      {text}
    </button>
  ) : (
    <span className={STATIC_CLASS}>
      {badge}
      {text}
    </span>
  );

  const hasReason = blocked && Boolean(reason);

  return (
    <li
      aria-current={state === 'current' ? 'step' : undefined}
      aria-disabled={blocked ? 'true' : undefined}
      data-step-state={state}
      className="inline-flex items-center gap-2"
    >
      {hasReason ? (
        <Tooltip content={reason} size="compact" position="bottom">
          {body}
        </Tooltip>
      ) : (
        body
      )}
      {hasReason ? <span className="sr-only">{reason}</span> : null}
      {isLast ? null : (
        <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-content-faint" />
      )}
    </li>
  );
}

StepperStep.propTypes = {
  stepId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  state: PropTypes.oneOf(['completed', 'current', 'upcoming']).isRequired,
  label: PropTypes.node.isRequired,
  isLast: PropTypes.bool,
  interactive: PropTypes.bool,
  blocked: PropTypes.bool,
  reason: PropTypes.string,
  onSelect: PropTypes.func,
};

export default memo(StepperStep);
